/*
  MyOS.jsx
  ----------------
  Interface d'affichage des OS.
*/
import { useCallback, useEffect, useState } from "react";
import {
  Stack,
  Card,
  Text,
  Group,
  Button,
  Center,
  Grid,
  Divider,
  Badge,
  Box,
  LoadingOverlay,
  Modal,
  TextInput,
  Select,
  Textarea,
  Title,
  Loader,
} from "@mantine/core";
import { IconFile, IconEdit, IconTrash, IconSignature, IconDownload, IconBook, IconEye } from "@tabler/icons-react";
import { useUser } from "../tools/Context";
import { useNavigate, useLocation } from "react-router-dom";
import DisplayWorkflow from "../tools/DisplayWorkflow";
import { showNotification } from "@mantine/notifications";
import DisplayOsProof from "../tools/DisplayOsProof";
import { get, post, uploadFile } from "../tools/Request";
import DisplayOsFile from "../tools/DisplayOsFile";
import { Dropzone } from "@mantine/dropzone";
import downloadFromBackend from "../tools/DownloadFromBackend";

export default function MyOS() {
  const [osList, setOsList] = useState([]);
  const [modalOpened, setModalOpened] = useState(false);
  const [selectedOsId, setSelectedOsId] = useState(null);
  const [selectedOS, setSelectedOS] = useState(null);
  const [modalProof, setModalProofOpen] = useState(false);
  const [loadingProof, setLoadingProof] = useState(false);
  const [loadingCards, setLoadingCards] = useState({});
  const [filters, setFilters] = useState({ osName: "", osType: "", osStatus: "" });
  const [commentModalOpened, setCommentModalOpened] = useState(false);
  const [signModalSimpleOpened, setSignModalSimpleOpened] = useState(false);
  const [comment, setComment] = useState("");
  const [actionType, setActionType] = useState(null);
  const [loadingModal, setLoadingModal] = useState(false);
  const [linkedOsModalOpened, setLinkedOsModalOpened] = useState(false);
  const [linkedOsId, setLinkedOsId] = useState(null);
  const [attachmentModalOpened, setAttachmentModalOpened] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState([]);
  const [savingAttachments, setSavingAttachments] = useState(false);
  const [fileModalOpened, setFileModalOpened] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [loadingList, setLoadingList] = useState(true);

  const handleFileClick = useCallback((file) => {
    setSelectedFile(file);
    setFileModalOpened(true);
  }, []);

  const user = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { role, isratp, id: userId, email: userEmail } = useUser();

  const isHolder = role === "titulaire de marché";
  const primaryColor = isHolder ? "#ff7e2e" : isratp ? "#0a0082" : "#00aa91";

  const isBrouillonPage = location.pathname === "/brouillon";

  const isFirstSignerForUser = (os) => {
    const wf = (os.workflow || []).slice().sort((a, b) => a.order_index - b.order_index);
    const firstRequired = wf.find((w) => w.role === "signer");
    if (!firstRequired) return false;
    if (userId && firstRequired.signer_id) return firstRequired.signer_id === userId;
    if (userEmail && firstRequired.email) {
      return String(firstRequired.email).toLowerCase() === String(userEmail).toLowerCase();
    }
    return false;
  };

  const isLastSignerForUser = (os) => {
    const wfSigners = (os.workflow || [])
      .filter((w) => w.role === "signer")
      .sort((a, b) => a.order_index - b.order_index);
    if (wfSigners.length === 0) return false;
    const last = wfSigners[wfSigners.length - 1];
    if (userId && last.signer_id) return last.signer_id === userId;
    if (userEmail && last.email) {
      return String(last.email).toLowerCase() === String(userEmail).toLowerCase();
    }
    return false;
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const osNameFromUrl = queryParams.get("osName");
    if (osNameFromUrl) setFilters((prev) => ({ ...prev, osName: osNameFromUrl }));
    fetchOSList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.token, location.search, location.pathname]);

  const fetchOSList = async () => {
    setLoadingList(true);
    try {
      const endpoint = isBrouillonPage ? "os/user-drafts" : "os/user";
      const response = await get(endpoint, null, user.token);
      const sortedOsList = response.sort(
        (a, b) =>
          new Date(b.last_edit_timestamp || b.created_at || 0) - new Date(a.last_edit_timestamp || a.created_at || 0)
      );
      setOsList(sortedOsList);
    } catch (error) {
      console.error("Error fetching OS list:", error);
      showNotification({ title: "Erreur", message: "Impossible de récupérer les OS.", color: "red" });
    } finally {
      setLoadingList(false);
    }
  };

  const handleLaunchWorkflow = async (osId, status, cmt = "") => {
    setLoadingModal(true);
    try {
      let statusText = "approved";
      if (actionType === "reserve") statusText = "signé avec réserve";
      else if (actionType === "refused") statusText = "rejected";
      else if (actionType === "accuse") statusText = cmt && cmt.trim().length > 0 ? "signé avec réserve" : "approved";

      await post("sign/sign", { osId, status: statusText, comment: cmt }, user.token);
      await fetchOSList();

      let message = "OS signé.";
      let color = "green";
      if (actionType === "refused") {
        message = "OS refusé.";
        color = "red";
      } else if (actionType === "accuse") {
        message = cmt?.trim() ? "Accusé de réception avec réserve." : "Accusé de réception.";
        color = cmt?.trim() ? "yellow" : "green";
      } else if (actionType === "reserve") {
        message = "OS signé avec réserve.";
        color = "yellow";
      }
      showNotification({ title: "Succès", message, color });
    } catch (error) {
      console.error("Error updating OS:", error);
      showNotification({
        title: "Erreur",
        message: "Une erreur est survenue lors de la signature de l'OS.",
        color: "red",
      });
    } finally {
      setLoadingModal(false);
      setCommentModalOpened(false);
      setModalOpened(false);
      setSignModalSimpleOpened(false);
      setComment("");
      setActionType(null);
    }
  };

  const handleDisplayProof = (os) => {
    setSelectedOS(os);
    setModalProofOpen(true);
    setLoadingProof(true);
    setTimeout(() => setLoadingProof(false), 1200);
  };

  const handleViewDetails = (osId, os) => {
    setSelectedOsId(osId);
    setSelectedOS(os);
    setModalOpened(true);
  };

  const handleDeleteOsConfirmed = async (osId) => {
    setLoadingCards((prev) => ({ ...prev, [osId]: true }));
    try {
      await post(`os/delete/`, { osId: osId }, user.token, {});
      setOsList(osList.filter((os) => os.id !== osId));
      showNotification({ title: "Success", message: "L'OS a bien été supprimé", color: "green" });
    } catch (error) {
      console.error("Error deleting OS:", error);
      showNotification({ title: "Error", message: "Erreur lors de la suppression de l'OS", color: "red" });
    } finally {
      setLoadingCards((prev) => ({ ...prev, [osId]: false }));
      setConfirmationModalOpen(false);
    }
  };

  const handleDeleteOS = (osId) => {
    setConfirmationModalOpen(true);
    setSelectedOsId(osId);
  };

  const handleEditOS = (osId) => navigate(`/createOS?osId=${osId}`);

  const handleViewLinkedOS = (linkedOsId) => {
    setLinkedOsId(linkedOsId);
    setLinkedOsModalOpened(true);
  };

  const handleSaveAttachments = async () => {
    if (!selectedOsId) {
      showNotification({ title: "Error", message: "OS ID not selected", color: "red" });
      return;
    }
    if (droppedFiles.length === 0) {
      showNotification({ title: "Error", message: "Aucun fichier sélectionné", color: "red" });
      return;
    }

    const formData = new FormData();
    formData.append("osId", selectedOsId);
    formData.append("type", "");
    droppedFiles.forEach((file) => formData.append("files", file));

    try {
      setSavingAttachments(true);
      await uploadFile("file/save-document-blockchain", formData, user.token);
      showNotification({ title: "Success", message: "Documents ajoutés avec succès", color: "green" });
      setAttachmentModalOpened(false);
      fetchOSList();
    } catch (error) {
      console.error("Error uploading attachments:", error);
      showNotification({ title: "Error", message: "Erreur lors du téléchargement des pièces jointes", color: "red" });
    } finally {
      setSavingAttachments(false);
    }
  };

  const filteredOsList = isBrouillonPage
    ? osList.filter((os) => !os.saved)
    : osList.filter((os) => {
        const matchesName = os.os_name?.toLowerCase().includes(filters.osName.toLowerCase());
        const matchesType = filters.osType ? os.os_type === filters.osType : true;

        const anySigned = os.workflow?.some((w) => w.role === "signer" && w.signed) ?? false;
        const allSigned = os.all_signed === true;

        const matchesStatus = filters.osStatus
          ? (filters.osStatus === "en cours de signature" && anySigned && !allSigned) ||
            (filters.osStatus === "signé" && allSigned) ||
            (filters.osStatus === "autre" && !anySigned && !allSigned)
          : true;

        return matchesName && matchesType && matchesStatus;
      });

  return (
    <>
      <Center>
        <Title>{isBrouillonPage ? "Mes brouillons d'OS" : "Mes OS"}</Title>
      </Center>
      <Stack spacing="lg" mt="md">
        {loadingList ? (
          <Center>
            <Box mt="xl">
              <Loader color={isratp ? "#0a0082" : "#ff7e2e"} size="lg" />
            </Box>
          </Center>
        ) : (
          <>
            {filteredOsList.length === 0 && (
              <Text align="center" size="lg" color="dimmed">
                Aucun OS trouvé avec les critères sélectionnés.
              </Text>
            )}

            {!isBrouillonPage && (
              <Group grow>
                <TextInput
                  placeholder="Nom de l'OS"
                  value={filters.osName}
                  onChange={(e) => setFilters({ ...filters, osName: e.target.value })}
                />
                <Select
                  placeholder="Type d'OS"
                  value={filters.osType}
                  onChange={(value) => setFilters({ ...filters, osType: value })}
                  data={[
                    { value: "OS de TS", label: "OS de TS" },
                    { value: "OS de prix", label: "OS de prix" },
                    { value: "OS de moins-value", label: "OS de moins-value" },
                    { value: "OS autre", label: "OS autre" },
                  ]}
                />
                <Select
                  placeholder="Statut de l'OS"
                  value={filters.osStatus}
                  onChange={(value) => setFilters({ ...filters, osStatus: value })}
                  data={[
                    { value: "en cours de signature", label: "En cours de signature" },
                    { value: "signé", label: "Signé" },
                    { value: "autre", label: "Autre" },
                  ]}
                />
              </Group>
            )}

            {filteredOsList.map((os, index) => {
              const userIsFirstSigner = isFirstSignerForUser(os);
              const userHasSigned = !!os.user_has_signed;

              const canEditDraft = isBrouillonPage && !os.saved && (role === "administrateur" || os.user_id === userId);

              return (
                <Center key={index}>
                  {(isBrouillonPage ? !os.saved : os.saved) && (
                    <Card
                      shadow="md"
                      padding="lg"
                      radius="lg"
                      withBorder
                      style={{ width: "100%", position: "relative", overflow: "hidden" }}
                    >
                      <LoadingOverlay visible={loadingCards[os.id]} loaderProps={{ children: "Chargement" }} />

                      {/* Watermark for drafts */}
                      {!os.saved && (
                        <Box
                          style={{
                            position: "absolute",
                            inset: 0,
                            pointerEvents: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: 0.08,
                            transform: "rotate(-20deg)",
                            userSelect: "none",
                          }}
                        >
                          <Text
                            style={{
                              fontSize: 120,
                              fontWeight: 800,
                              letterSpacing: 8,
                              lineHeight: 1,
                              textTransform: "uppercase",
                            }}
                          >
                            Brouillon
                          </Text>
                        </Box>
                      )}

                      <Grid>
                        <Grid.Col span={8}>
                          <Group position="apart" mb="xs">
                            <Text weight={600} size="lg">
                              {os.os_type} {os.os_name}
                            </Text>

                            <Group spacing="xs">
                              {!os.saved && (
                                <Badge color="gray" variant="dot">
                                  Brouillon
                                </Badge>
                              )}
                              <Badge
                                color={primaryColor}
                                leftSection={<IconEye size={18} />}
                                variant="light"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleViewDetails(os.id, os)}
                              >
                                Voir l'OS
                              </Badge>

                              {os.in_progress && (
                                <Badge
                                  color={primaryColor}
                                  leftSection={<IconBook size={18} />}
                                  variant="light"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleDisplayProof(os)}
                                >
                                  Historique de l'OS
                                </Badge>
                              )}
                            </Group>
                          </Group>

                          <Text
                            size="sm"
                            color="dimmed"
                            mb="md"
                            style={{ cursor: "pointer", textDecoration: "underline" }}
                            onClick={() => navigate(`/MarketDisplay?market_number=${os.market_number}`)}
                          >
                            Voir le marché {os.market_number}
                          </Text>

                          <Text size="sm" c="dimmed">
                            Dernière modification :{" "}
                            <strong>
                              {os.last_edit_timestamp ? new Date(os.last_edit_timestamp).toLocaleString("fr-FR") : "—"}
                            </strong>
                          </Text>
                          {os.linked_os_name && (
                            <Text
                              size="sm"
                              color="dimmed"
                              mb="md"
                              style={{ cursor: "pointer", textDecoration: "underline" }}
                              component="a"
                              href={`/myOs?osName=${os.linked_os_name}`}
                              onClick={(e) => {
                                e.preventDefault();
                                handleViewLinkedOS(os.linkedos);
                              }}
                            >
                              OS lié : {os.linked_os_name}
                            </Text>
                          )}
                        </Grid.Col>

                        <Divider size="sm" orientation="vertical" />

                        <Grid.Col span={3.9}>
                          <Stack>
                            <Group justify="space-between">
                              <Text size="sm" weight={500}>
                                Pièces jointes
                              </Text>

                              {!isBrouillonPage && role !== "administrateur" && (
                                <Badge
                                  color={primaryColor}
                                  leftSection={<IconDownload size={18} />}
                                  variant="light"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    setSelectedOsId(os.id);
                                    setAttachmentModalOpened(true);
                                  }}
                                >
                                  Ajouter une pièce jointe
                                </Badge>
                              )}
                            </Group>

                            {os.uploadedFiles?.length > 0 ? (
                              <Box
                                style={{
                                  maxHeight: os.uploadedFiles.length > 4 ? "120px" : "auto",
                                  overflowY: os.uploadedFiles.length > 4 ? "auto" : "visible",
                                }}
                              >
                                {os.uploadedFiles.map((file, i) => (
                                  <Group key={i} spacing="xs" mt="xs" onClick={() => handleFileClick(file)}>
                                    <IconFile size={16} />
                                    <Text style={{ cursor: "pointer" }} size="sm" color="blue" underline>
                                      {file.name}
                                    </Text>
                                  </Group>
                                ))}
                              </Box>
                            ) : (
                              <Text size="sm" color="dimmed">
                                Aucun fichier joint.
                              </Text>
                            )}
                          </Stack>
                        </Grid.Col>
                      </Grid>

                      {!isHolder && os.saved && os.workflow?.length > 0 && (
                        <>
                          <Divider mt="md" mb="sm" />
                          <DisplayWorkflow os={os} />
                        </>
                      )}

                      <Divider mt="md" mb="sm" />

                      <Group justify="center" gap="md">
                        {canEditDraft && (
                          <>
                            <Button
                              leftSection={<IconEdit size={18} />}
                              variant="filled"
                              color={primaryColor}
                              radius="md"
                              onClick={() => handleEditOS(os.id)}
                            >
                              Modifier
                            </Button>
                            <Button
                              leftSection={<IconTrash size={18} />}
                              variant="filled"
                              color="#e2231a"
                              radius="md"
                              onClick={() => handleDeleteOS(os.id)}
                            >
                              Supprimer
                            </Button>
                          </>
                        )}

                        {!isBrouillonPage && !userHasSigned && role !== "administrateur" && userIsFirstSigner && (
                          <>
                            <Button
                              leftSection={<IconEdit size={18} />}
                              variant="filled"
                              color={primaryColor}
                              radius="md"
                              onClick={() => handleEditOS(os.id)}
                            >
                              Modifier
                            </Button>
                            <Button
                              leftSection={<IconTrash size={18} />}
                              variant="filled"
                              color="#e2231a"
                              radius="md"
                              onClick={() => handleDeleteOS(os.id)}
                            >
                              Supprimer
                            </Button>
                          </>
                        )}

                        {!isBrouillonPage && os.user_is_next_signer && (
                          <Button
                            variant="filled"
                            color={isratp ? "#00aa91" : "#ff7e2e"}
                            radius="md"
                            onClick={() => handleViewDetails(os.id, os)}
                            leftSection={<IconSignature size={18} />}
                          >
                            Voir l'OS et signer
                          </Button>
                        )}
                      </Group>
                    </Card>
                  )}
                </Center>
              );
            })}

            <Modal size={900} opened={modalOpened} onClose={() => setModalOpened(false)} title="Détails de l'OS">
              {selectedOsId && selectedOS && <DisplayOsFile osID={selectedOsId} />}
              {selectedOsId && selectedOS && selectedOS.user_is_next_signer && !selectedOS.user_has_signed && (
                <Center>
                  {isFirstSignerForUser(selectedOS) ? (
                    <Button
                      mt={10}
                      ml={10}
                      variant="light"
                      color="#00aa91"
                      radius="md"
                      loading={loadingModal}
                      onClick={() => {
                        setActionType(null);
                        handleLaunchWorkflow(selectedOsId, true, "");
                      }}
                    >
                      Signer
                    </Button>
                  ) : isLastSignerForUser(selectedOS) ? (
                    <Button
                      mt={10}
                      ml={10}
                      variant="light"
                      color={primaryColor}
                      radius="md"
                      loading={loadingModal}
                      onClick={() => {
                        setActionType("accuse");
                        setCommentModalOpened(true);
                      }}
                    >
                      Accuser réception
                    </Button>
                  ) : (
                    <>
                      <Button
                        mt={10}
                        ml={10}
                        variant="light"
                        color="#00aa91"
                        radius="md"
                        loading={loadingModal}
                        onClick={() => handleLaunchWorkflow(selectedOsId, true)}
                      >
                        Signer sans réserve
                      </Button>
                      <Button
                        mt={10}
                        ml={10}
                        variant="light"
                        color="#00aa91"
                        radius="md"
                        loading={loadingModal}
                        onClick={() => {
                          setActionType("reserve");
                          setCommentModalOpened(true);
                        }}
                      >
                        Signer avec réserve
                      </Button>
                      <Button
                        mt={10}
                        ml={10}
                        variant="light"
                        color="#e2231a"
                        radius="md"
                        loading={loadingModal}
                        onClick={() => {
                          setActionType("refused");
                          setCommentModalOpened(true);
                        }}
                      >
                        Refuser
                      </Button>
                    </>
                  )}
                </Center>
              )}
            </Modal>

            <Modal
              size={900}
              opened={signModalSimpleOpened}
              onClose={() => setSignModalSimpleOpened(false)}
              title="Signer l'OS"
            >
              {selectedOsId && <DisplayOsFile osID={selectedOsId} />}
              {!isHolder && selectedOS && (
                <>
                  <Divider mt="md" mb="sm" />
                  <DisplayWorkflow os={selectedOS} />
                </>
              )}
              {selectedOsId && (
                <Center>
                  <Button
                    mt={10}
                    ml={10}
                    variant="light"
                    color={primaryColor}
                    radius="md"
                    loading={loadingModal}
                    onClick={() => handleLaunchWorkflow(selectedOsId, true)}
                    disabled={!(selectedOS?.user_is_next_signer && !selectedOS?.user_has_signed)}
                    title={
                      !(selectedOS?.user_is_next_signer && !selectedOS?.user_has_signed)
                        ? selectedOS?.user_has_signed
                          ? "Vous avez déjà signé."
                          : "Ce n'est pas votre tour de signer."
                        : ""
                    }
                  >
                    Signer l'OS et l'envoyer dans le workflow de signature
                  </Button>
                </Center>
              )}
            </Modal>

            <Modal size={1000} opened={modalProof} onClose={() => setModalProofOpen(false)} title="Historique de l'OS">
              {selectedOS && <DisplayOsProof osData={selectedOS} />}
            </Modal>

            <Modal
              size={700}
              opened={commentModalOpened}
              onClose={() => {
                setCommentModalOpened(false);
                setComment("");
                setActionType(null);
              }}
              title={
                actionType === "accuse"
                  ? "Accuser réception (réserve optionnelle)"
                  : actionType === "reserve"
                  ? "Signer avec réserve"
                  : "Refuser"
              }
            >
              <Textarea
                label={actionType === "accuse" ? "Réserve (facultatif)" : "Commentaire"}
                placeholder={actionType === "accuse" ? "Ajoutez une réserve (facultatif)" : "Entrez votre commentaire"}
                value={comment}
                autosize
                minRows={4}
                maxRows={4}
                onChange={(e) => setComment(e.target.value)}
              />
              <Button
                mt="md"
                variant="filled"
                color={primaryColor}
                radius="md"
                loading={loadingModal}
                onClick={() => handleLaunchWorkflow(selectedOsId, true, comment)}
              >
                Envoyer
              </Button>
            </Modal>

            <Modal size={900} opened={linkedOsModalOpened} onClose={() => setLinkedOsModalOpened(false)} title="OS lié">
              {linkedOsId && <DisplayOsFile osID={linkedOsId} />}
            </Modal>

            <Modal
              opened={attachmentModalOpened}
              onClose={() => setAttachmentModalOpened(false)}
              title="Ajouter une pièce jointe"
              size={600}
            >
              <Dropzone
                onDrop={(files) => setDroppedFiles(files)}
                onReject={(files) => console.log("Fichiers rejetés :", files)}
                maxSize={5 * 1024 ** 2}
              >
                {droppedFiles.length === 0 ? (
                  <Center>
                    <Text size="md" c="dimmed">
                      Déposez la pièce jointe ici
                    </Text>
                  </Center>
                ) : (
                  <Center>
                    {droppedFiles.map((file, index) => (
                      <Text key={index} size="md" c="dimmed">
                        {file.name}
                      </Text>
                    ))}
                  </Center>
                )}
              </Dropzone>

              <Button
                color={primaryColor}
                mt="md"
                variant="filled"
                onClick={handleSaveAttachments}
                loading={savingAttachments}
              >
                Enregistrer
              </Button>
            </Modal>

            <Modal size={500} opened={fileModalOpened} onClose={() => setFileModalOpened(false)} title="Fichier">
              {selectedFile && (
                <Center>
                  <Text size="lg">{selectedFile.name}</Text>
                </Center>
              )}
              <Group position="center" mt="md">
                <Button variant="light" color={primaryColor} onClick={() => downloadFromBackend(selectedFile.id)}>
                  Télécharger le document
                </Button>
              </Group>
            </Modal>

            <Modal
              opened={confirmationModalOpen}
              onClose={() => setConfirmationModalOpen(false)}
              title="Confirmer la suppression"
            >
              <p>Êtes-vous sûr de vouloir supprimer cet OS ?</p>
              <Button
                color="#e2231a"
                onClick={() => handleDeleteOsConfirmed(selectedOsId)}
                style={{ marginTop: "1rem" }}
              >
                Supprimer
              </Button>
              <Button
                variant="light"
                onClick={() => setConfirmationModalOpen(false)}
                style={{ marginTop: "1rem", marginLeft: "1rem" }}
              >
                Annuler
              </Button>
            </Modal>
          </>
        )}
      </Stack>
    </>
  );
}
